import type { OfflineLedgerEntry } from './offline-queue'
import {
  createOfflineLedger,
  isOwnedOfflineEnvelope,
  OfflineWriteIntentConflictError,
} from './offline-queue'

export interface OfflineStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
  removeItem(key: string): Promise<void>
}

export class OfflineLedgerStorageError extends Error {
  constructor() {
    super(
      'Saved changes could not be restored safely. Their original data has been kept.',
    )
    this.name = 'OfflineLedgerStorageError'
  }
}

const statuses = new Set([
  'queued',
  'inflight',
  'succeeded',
  'failed',
  'conflicted',
  'needs-review',
  'expired',
])

function isStoredEntry(
  value: unknown,
  ownerId: string,
): value is OfflineLedgerEntry {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<OfflineLedgerEntry>
  return (
    isOwnedOfflineEnvelope(entry.envelope) &&
    entry.envelope.ownerId === ownerId &&
    typeof entry.envelope.label === 'string' &&
    typeof entry.status === 'string' &&
    statuses.has(entry.status) &&
    typeof entry.attempts === 'number' &&
    Number.isSafeInteger(entry.attempts) &&
    entry.attempts >= 0 &&
    typeof entry.updatedAt === 'number' &&
    Number.isFinite(entry.updatedAt) &&
    entry.updatedAt >= 0 &&
    (entry.completionOnly === undefined ||
      (entry.status === 'succeeded' && entry.completionOnly === true)) &&
    (entry.message === undefined || typeof entry.message === 'string')
  )
}

/** A user's outbox survives sign-out; it is only restored for its original owner. */
export function createOwnedOfflineLedger(
  storage: OfflineStorage,
  legacyKey: string,
) {
  const knownOwners = new Set<string>()
  const unreadableOwners = new Set<string>()
  let writes = Promise.resolve()
  const keyFor = (ownerId: string) =>
    `${legacyKey}:v2:${encodeURIComponent(ownerId)}`
  const ledger = createOfflineLedger(entries => {
    const groups = new Map<string, OfflineLedgerEntry[]>()
    for (const entry of entries) {
      if (!isOwnedOfflineEnvelope(entry.envelope)) continue
      const owner = entry.envelope.ownerId
      knownOwners.add(owner)
      const group = groups.get(owner)
      if (group) group.push(entry)
      else groups.set(owner, [entry])
    }
    const snapshots = [...knownOwners].map(
      owner => [owner, keyFor(owner), groups.get(owner) ?? []] as const,
    )
    writes = writes
      .catch(() => undefined)
      .then(async () => {
        for (const [owner, key, entries] of snapshots) {
          if (unreadableOwners.has(owner)) throw new OfflineLedgerStorageError()
          if (entries.length)
            await storage.setItem(
              key,
              JSON.stringify(
                entries.map(entry => ({
                  envelope: entry.envelope,
                  status: entry.status,
                  attempts: entry.attempts,
                  updatedAt: entry.updatedAt,
                  message: entry.message,
                  ...(entry.status === 'succeeded'
                    ? { completionOnly: true }
                    : {}),
                })),
              ),
            )
          else await storage.removeItem(key)
        }
      })
    // Preserve rejection for flush(); background writes must not become unhandled.
    void writes.catch(() =>
      console.warn('[OfflineQueue] Persistence could not be confirmed'),
    )
  })

  return {
    ledger,
    flush: () => writes,
    async restore(ownerId: string | null): Promise<void> {
      // Old work cannot be attributed safely. Keep its original bytes for
      // recovery, but never expose or automatically replay them under a login.
      const legacy = await storage.getItem(legacyKey)
      if (legacy !== null) {
        await storage.setItem(`${legacyKey}:quarantine-v1`, legacy)
        await storage.removeItem(legacyKey)
      }
      if (!ownerId) return
      await writes.catch(() => undefined)
      const raw = await storage.getItem(keyFor(ownerId))
      if (raw === null) return
      // A partial restore followed by persistence would erase rejected rows.
      // Validate the entire owner snapshot and retain its bytes on any failure.
      try {
        const parsed: unknown = JSON.parse(raw)
        if (
          !Array.isArray(parsed) ||
          !parsed.every(entry => isStoredEntry(entry, ownerId))
        )
          throw new OfflineLedgerStorageError()
        if (
          new Set(parsed.map(entry => entry.envelope.idempotencyKey)).size !==
          parsed.length
        )
          throw new OfflineWriteIntentConflictError()
        ledger.hydrate(parsed)
      } catch (error) {
        unreadableOwners.add(ownerId)
        throw error instanceof SyntaxError
          ? new OfflineLedgerStorageError()
          : error
      }
      unreadableOwners.delete(ownerId)
      knownOwners.add(ownerId)
      ledger.prune()
    },
  }
}
